import React, { useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Divider,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import EmailIcon from "@mui/icons-material/Email";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTheme } from "@mui/material/styles";

interface FooterSuporteProps {
  /** "floating" = botões grandes fixos (padrão) | "inline" = botão "Suporte" p/ header */
  variant?: "floating" | "inline";
  /** no modo inline, renderiza só o ícone (mais discreto) */
  iconOnly?: boolean;
}

export const FooterSuporte = ({ variant = "floating", iconOnly = false }: FooterSuporteProps) => {
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuAberto = Boolean(menuAnchor);

  const email = "sinsoft@hotmail.com";

  const abrirWhatsApp = () => {
    window.open("https://wa.me/5433431167", "_blank");
    setMenuAnchor(null);
  };

  const abrirEmail = () => {
    setOpenEmailModal(true);
    setMenuAnchor(null);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const inline = variant === "inline";

  const copiarEmail = async () => {
    await navigator.clipboard.writeText(email);
    setSnackbarOpen(true);
  };

  const paperSx = useMemo(() => {
    return {
      position: "fixed",
      // no mobile fica mais “justinho” e não sobe tanto
      bottom: isMobile ? 64 : 70,
      left: 0,
      width: "100%",
      zIndex: 1300,
      backgroundColor: "transparent",

      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",

      // menos espaço no mobile
      paddingY: isMobile ? 0.5 : 1,
      paddingX: isMobile ? 1 : 4,
      gap: isMobile ? 0.75 : 1,
      pointerEvents: "none", // deixa só os botões clicáveis
    } as const;
  }, [isMobile]);

  const buttonSx = useMemo(() => {
    if (inline) {
      return {
        pointerEvents: "auto",
        width: 40,
        height: 40,
        borderRadius: 2,
      } as const;
    }
    return {
      pointerEvents: "auto",
      // botões menores no mobile
      width: isMobile ? 42 : 54,
      height: isMobile ? 42 : 54,
      borderRadius: isMobile ? 2.5 : 3,
      boxShadow: isMobile ? 1 : 2,
    } as const;
  }, [isMobile, inline]);

  // botões grandes flutuantes (variante "floating")
  const botoesFlutuantes = (
    <>
      <Tooltip title="Suporte via WhatsApp">
        <IconButton
          sx={{
            ...buttonSx,
            backgroundColor: "#25D366",
            color: "#fff",
            "&:hover": { backgroundColor: "#1ebe5d" },
          }}
          onClick={() => window.open("https://wa.me/5433431167", "_blank")}
        >
          <WhatsAppIcon fontSize={isMobile ? "medium" : "large"} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Suporte por e-mail">
        <IconButton
          sx={{
            ...buttonSx,
            backgroundColor: "#1976d2",
            color: "#fff",
            "&:hover": { backgroundColor: "#115293" },
          }}
          onClick={() => setOpenEmailModal(true)}
        >
          <SupportAgentIcon fontSize={isMobile ? "medium" : "large"} />
        </IconButton>
      </Tooltip>
    </>
  );

  // botão único "Suporte" com menu (variante "inline")
  const botaoSuporte = (
    <>
      {iconOnly ? (
        <Tooltip title="Suporte">
          <IconButton
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ color: "primary.main" }}
          >
            <SupportAgentIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="contained"
          startIcon={<SupportAgentIcon />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            color: "#fff",
            background: "linear-gradient(135deg, #2563eb, #4f8ef7)",
            boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
            "&:hover": {
              background: "linear-gradient(135deg, #1e40af, #3b82f6)",
              boxShadow: "0 6px 16px rgba(37,99,235,0.45)",
            },
            // no mobile mostra só o ícone pra economizar espaço
            minWidth: { xs: 44, sm: "auto" },
            px: { xs: 1.2, sm: 2.2 },
            py: { xs: 1, sm: 0.9 },
            "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 } },
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Suporte
          </Box>
        </Button>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={menuAberto}
        onClose={() => setMenuAnchor(null)}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        PaperProps={{ sx: { mt: 1, minWidth: 220, borderRadius: 2 } }}
      >
        <Typography
          variant="caption"
          sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
        >
          Fale com o suporte
        </Typography>
        <MenuItem onClick={abrirWhatsApp}>
          <ListItemIcon>
            <WhatsAppIcon sx={{ color: "#25D366" }} />
          </ListItemIcon>
          <ListItemText primary="WhatsApp" secondary="Atendimento rápido" />
        </MenuItem>
        <MenuItem onClick={abrirEmail}>
          <ListItemIcon>
            <EmailIcon sx={{ color: "#1976d2" }} />
          </ListItemIcon>
          <ListItemText primary="E-mail" secondary={email} />
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <>
      {inline ? (
        botaoSuporte
      ) : (
        <Paper elevation={0} sx={paperSx}>
          {botoesFlutuantes}
        </Paper>
      )}

      {/* Modal de E-mail */}
      <Dialog
        open={openEmailModal}
        onClose={() => setOpenEmailModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>E-mail Sinsoft</DialogTitle>

        <Divider sx={{ mx: 3, mb: 2 }} />

        <DialogContent sx={{ pt: 0, pb: 1, px: 3 }}>
          <Stack spacing={2}>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="justify"
              sx={{ fontSize: isMobile ? 13 : 14 }}
            >
              Este é o nosso e-mail oficial de atendimento. Utilize-o para
              solicitar suporte, esclarecer dúvidas ou relatar qualquer problema
              relacionado ao sistema.
              <br />
              <strong>Nossa equipe responderá o mais breve possível.</strong>
            </Typography>

            <TextField
              label="E-mail para contato"
              value={email}
              fullWidth
              size={isMobile ? "small" : "medium"}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton
                    onClick={copiarEmail}
                    edge="end"
                    size="small"
                    title="Copiar e-mail"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setOpenEmailModal(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: isMobile ? "center" : "right",
        }}
        sx={{
          mb: isMobile ? 8 : 2, // sobe um pouco pra não ficar colado no bottom do celular
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ fontSize: isMobile ? 13 : 14 }}
        >
          E-mail copiado para a área de transferência
        </Alert>
      </Snackbar>
    </>
  );
};
